"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";

export type SplitMode = "chars" | "words";
export type Numbering = "none" | "suffix" | "prefix";

const WORD_SPLIT_RE = /\s+/;

type Strings = { [K in keyof typeof TEXT_SPLITTER_STRINGS]: string };

const PRESETS: { label: keyof Strings; value: number }[] = [
  { label: "presetXPost", value: 280 },
  { label: "presetSms", value: 160 },
  { label: "presetInstagram", value: 2200 },
];

const NUMBERING_OPTIONS: { value: Numbering; label: keyof Strings }[] = [
  { value: "none", label: "numberingNone" },
  { value: "suffix", label: "numberingSuffix" },
  { value: "prefix", label: "numberingPrefix" },
];

function getWords(text: string): string[] {
  return text
    .trim()
    .split(WORD_SPLIT_RE)
    .filter((w) => w.length > 0);
}

function chunkByChars(text: string, max: number): string[] {
  const chunks: string[] = [];
  let current = "";
  for (const word of getWords(text)) {
    if (current.length === 0) {
      current = word;
    } else if (current.length + 1 + word.length <= max) {
      current = `${current} ${word}`;
    } else {
      chunks.push(current);
      current = word;
    }
    while (current.length > max) {
      chunks.push(current.slice(0, max));
      current = current.slice(max);
    }
  }
  if (current.length > 0) {
    chunks.push(current);
  }
  return chunks;
}

function chunkByWords(text: string, max: number): string[] {
  const words = getWords(text);
  const chunks: string[] = [];
  for (let i = 0; i < words.length; i += max) {
    chunks.push(words.slice(i, i + max).join(" "));
  }
  return chunks;
}

/** The label for part `index` of `total`, e.g. "(2/4)". */
function partLabel(index: number, total: number): string {
  return `(${index + 1}/${total})`;
}

/** Width of the longest label when each number has `digits` digits: "(NN/NN)" and a space. */
function labelWidth(digits: number): number {
  return 4 + 2 * digits;
}

function applyNumbering(chunks: string[], numbering: Numbering): string[] {
  if (numbering === "none" || chunks.length === 0) {
    return chunks;
  }
  const total = chunks.length;
  return chunks.map((chunk, i) => {
    const label = partLabel(i, total);
    if (numbering === "prefix") {
      return `${label} ${chunk}`;
    }
    return `${chunk} ${label}`;
  });
}

/**
 * Split `text` into parts that, label included, stay within `max` characters (or words).
 *
 * The label takes room from every part, and how much depends on how many parts there are
 * ("(1/9)" against "(10/12)"), which depends on how much room is left. So the room
 * is reserved for a label of N digits, and N grows until the part count fits it. If the
 * limit is smaller than the label itself, parts hold one character (or word) and run over.
 */
export function splitText(
  text: string,
  mode: SplitMode,
  max: number,
  numbering: Numbering
): string[] {
  const split = mode === "chars" ? chunkByChars : chunkByWords;
  if (numbering === "none") {
    return split(text, max);
  }
  for (let digits = 1; ; digits++) {
    const reserved = mode === "chars" ? labelWidth(digits) : 1;
    const chunks = split(text, Math.max(1, max - reserved));
    if (String(chunks.length).length <= digits || reserved >= max) {
      return applyNumbering(chunks, numbering);
    }
  }
}

/** English strings. A translated content file overrides these through `ui`. */
export const TEXT_SPLITTER_STRINGS = {
  splitBy: "Split by",
  maxCharacters: "Max characters",
  maxWords: "Max words",
  maximumCharactersPerChunk: "Maximum characters per chunk",
  maximumWordsPerChunk: "Maximum words per chunk",
  partNumbering: "Part numbering",
  textToSplit: "Text to split",
  pasteYourLongTextHere: "Paste your long text here…",
  charactersCount: "one:{count} character|other:{count} characters",
  wordsCount: "one:{count} word|other:{count} words",
  inputStats: "{characters} · {words}",
  splitText: "Split Text",
  clear: "Clear",
  chunk: "one: chunk|other: chunks",
  copied: "Copied!",
  copy: "Copy",
  presetXPost: "280 (X post)",
  presetSms: "160 (SMS)",
  presetInstagram: "2200 (Instagram)",
  numberingNone: "No numbering",
  numberingSuffix: "Suffix — text (1/4)",
  numberingPrefix: "Prefix — (1/4) text",
} as const;

export default function TextSplitterWidget() {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<SplitMode>("chars");
  const [maxSize, setMaxSize] = useState(280);
  const [numbering, setNumbering] = useState<Numbering>("none");
  const [chunks, setChunks] = useState<{ id: string; text: string }[]>([]);
  const [copiedIndex, setCopiedIndex] = useState(-1);

  function handleSplit() {
    if (input.trim().length === 0 || maxSize < 1) {
      return;
    }
    const parts = splitText(input, mode, maxSize, numbering);
    // The ids are positions on purpose: two identical parts must not share a React key.
    setChunks(parts.map((text, i) => ({ id: `part-${i}`, text })));
    setCopiedIndex(-1);
  }

  function handleCopyChunk(index: number) {
    const chunk = chunks[index];
    if (chunk === undefined) {
      return;
    }
    writeToClipboard(chunk.text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(-1), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setChunks([]);
    setCopiedIndex(-1);
  }

  function handlePreset(value: number) {
    setMode("chars");
    setMaxSize(value);
  }

  const locale = useToolLocale();
  const strings = useToolUi(TEXT_SPLITTER_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Split mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.splitBy}</p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "chars"}
              className="accent-primary"
              name="split-mode"
              onChange={() => setMode("chars")}
              type="radio"
              value="chars"
            />
            {strings.maxCharacters}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "words"}
              className="accent-primary"
              name="split-mode"
              onChange={() => setMode("words")}
              type="radio"
              value="words"
            />
            {strings.maxWords}
          </label>
        </div>
      </section>
      {/* Size + presets */}
      <section className="space-y-3">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="max-size"
        >
          {mode === "chars"
            ? strings.maximumCharactersPerChunk
            : strings.maximumWordsPerChunk}
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="max-size"
            min={1}
            onChange={(e) => setMaxSize(Number(e.target.value))}
            type="number"
            value={maxSize}
          />
          {PRESETS.map((p) => (
            <button
              className="rounded-lg border border-border bg-card px-3 py-2 text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground"
              key={p.value}
              onClick={() => handlePreset(p.value)}
              type="button"
            >
              {strings[p.label]}
            </button>
          ))}
        </div>
      </section>
      {/* Numbering */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.partNumbering}
        </p>
        <div className="flex flex-wrap gap-3">
          {NUMBERING_OPTIONS.map((n) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={n.value}
            >
              <input
                checked={numbering === n.value}
                className="accent-primary"
                name="numbering"
                onChange={() => setNumbering(n.value)}
                type="radio"
                value={n.value}
              />
              {strings[n.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="split-input"
        >
          {strings.textToSplit}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="split-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.pasteYourLongTextHere}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {t(strings.inputStats, {
            characters: tPlural(strings.charactersCount, locale, input.length, {
              count: fmt(input.length),
            }),
            words: tPlural(strings.wordsCount, locale, getWords(input).length, {
              count: fmt(getWords(input).length),
            }),
          })}
        </p>
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleSplit}
          type="button"
        >
          {strings.splitText}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && chunks.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Chunks */}
      {chunks.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-lg">
            {fmt(chunks.length)}
            {tPlural(strings.chunk, locale, chunks.length)}
          </h2>
          <div className="space-y-4">
            {chunks.map((chunk, i) => (
              <div
                className="space-y-3 rounded-lg border border-border bg-card p-4"
                key={chunk.id}
              >
                <p className="whitespace-pre-wrap font-mono text-foreground text-sm leading-relaxed">
                  {chunk.text}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-xs">
                    {tPlural(
                      strings.charactersCount,
                      locale,
                      chunk.text.length,
                      { count: fmt(chunk.text.length) }
                    )}
                  </span>
                  <button
                    className="rounded-lg border border-border bg-background px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                    onClick={() => handleCopyChunk(i)}
                    type="button"
                  >
                    {copiedIndex === i ? strings.copied : strings.copy}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
